import { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'sonner';

/**
 * Compare-scents context.
 *
 * Stores up to N product IDs in localStorage so the user can pick scents
 * across the shop and product pages, then jump to /compare for a
 * side-by-side notes / longevity / price view. localStorage = no auth
 * required, persists across reloads, works for anonymous guests.
 */
const STORAGE_KEY = 'jsf_compare';
const MAX_ITEMS = 3;

const CompareContext = createContext({
  ids: [],
  add: () => {},
  remove: () => {},
  clear: () => {},
  has: () => false,
  max: MAX_ITEMS,
});

export function CompareProvider({ children }) {
  const [ids, setIds] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(ids)); } catch { /* blocked */ }
  }, [ids]);

  const add = (productId, productName) => {
    if (!productId) return;
    setIds(prev => {
      if (prev.includes(productId)) return prev;
      if (prev.length >= MAX_ITEMS) {
        toast.error(`Compare is limited to ${MAX_ITEMS} scents. Remove one first.`);
        return prev;
      }
      const next = [...prev, productId];
      toast.success(`${productName || 'Added'} → Compare (${next.length}/${MAX_ITEMS})`);
      return next;
    });
  };

  const remove = (productId) => setIds(prev => prev.filter(id => id !== productId));
  const clear = () => setIds([]);
  const has = (productId) => ids.includes(productId);

  return (
    <CompareContext.Provider value={{ ids, add, remove, clear, has, max: MAX_ITEMS }}>
      {children}
    </CompareContext.Provider>
  );
}

export const useCompare = () => useContext(CompareContext);
